import React, { useState, useEffect } from 'react';
import { Stock, AllergenInfo } from '../types';
import { AllergenManager } from './AllergenManager';
import './StockForm.css';

interface StockFormProps {
    stock?: Stock;
    onSubmit: (stock: Omit<Stock, 'id' | 'updatedAt'>) => Promise<void>;
    onCancel: () => void;
    isSubmitting: boolean;
}

const UNIT_OPTIONS = [
    'kg',
    'g',
    'L',
    'ml',
    'pcs',
    'pack',
    'box',
    'bag',
    'bottle',
    'can'
];

const StockForm: React.FC<StockFormProps> = ({ stock, onSubmit, onCancel, isSubmitting }) => {
    const [name, setName] = useState(stock?.name || '');
    const [quantity, setQuantity] = useState(stock?.quantity.toString() || '0');
    const [unit, setUnit] = useState(stock?.unit || 'kg');
    const [notes, setNotes] = useState(stock?.notes || '');
    const [allergens, setAllergens] = useState<AllergenInfo[]>(stock?.allergens || []);
    // Only default to true when creating NEW stock (stock is undefined)
    const [isActive, setIsActive] = useState(stock ? (stock.isActive ?? false) : true);
    const [sortOrder, setSortOrder] = useState(stock?.sortOrder?.toString() || '');
    const [isChips, setIsChips] = useState(stock?.isChips ?? false);

    useEffect(() => {
        if (stock) {
            setName(stock.name);
            setQuantity(stock.quantity.toString());
            setUnit(stock.unit || 'kg');
            setNotes(stock.notes || '');
            setAllergens(stock.allergens || []);
            setIsActive(stock.isActive ?? false);
            setSortOrder(stock.sortOrder?.toString() || '');
            setIsChips(stock.isChips ?? false);
        }
    }, [stock]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const stockData: Omit<Stock, 'id' | 'updatedAt'> = {
            name,
            quantity: parseFloat(quantity),
            unit,
            notes,
            allergens: allergens.length > 0 ? allergens : undefined,
            isActive,
            sortOrder: sortOrder ? parseInt(sortOrder, 10) : undefined,
            isChips
        };

        await onSubmit(stockData);
    };

    return (
        <form className="stock-form" onSubmit={handleSubmit}>
            <h3>{stock ? 'Edit Stock Item' : 'Create Stock Item'}</h3>

            <div className="form-group">
                <label htmlFor="name">
                    Stock Name <span className="required">*</span>
                </label>
                <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Tomato Sauce, Chicken Breast, Mozzarella"
                    required
                />
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="quantity">
                        Quantity <span className="required">*</span>
                    </label>
                    <input
                        id="quantity"
                        type="number"
                        step="0.01"
                        min="0"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        required
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="unit">Unit</label>
                    <select
                        id="unit"
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                    >
                        {UNIT_OPTIONS.map(unitOption => (
                            <option key={unitOption} value={unitOption}>
                                {unitOption}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="sortOrder">Order</label>
                    <input
                        id="sortOrder"
                        type="number"
                        step="10"
                        min="0"
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                        placeholder="e.g., 10, 20, 30..."
                    />
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional information (supplier, storage location, etc.)"
                    rows={3}
                />
            </div>

            <div className="form-group">
                <label>Allergens</label>
                <AllergenManager
                    allergens={allergens}
                    onChange={setAllergens}
                />
            </div>

            <div className="form-group">
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                    />
                    <span>Active (visible in product stock selection)</span>
                </label>
            </div>

            <div className="form-group">
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        checked={isChips}
                        onChange={(e) => setIsChips(e.target.checked)}
                    />
                    <span>Is Chips (orders with this item show chips cooking time notice)</span>
                </label>
            </div>

            <div className="form-actions">
                <button
                    type="button"
                    onClick={onCancel}
                    className="button secondary"
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="button primary"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Saving...' : (stock ? 'Update Stock' : 'Create Stock')}
                </button>
            </div>
        </form>
    );
};

export default StockForm;
